import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Phone, Mail, MapPin, MessageCircle, Send, Loader2 } from "lucide-react";
import { BUSINESS } from "../data/business";
import { track } from "../analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGIMES = ["MEI", "Simples Nacional", "Lucro Presumido", "Lucro Real", "Ainda não sei"];

export const ContactSection = () => {
    const [form, setForm] = useState({ name: "", email: "", phone: "", regime: "", message: "" });
    const [loading, setLoading] = useState(false);

    const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post(`${API}/leads`, form);
            track("interaction", { label: "form-submit", path: "/contato" });
            toast.success("Pedido enviado com sucesso!", {
                description: "A WBS entrará em contato com você em breve.",
            });
            setForm({ name: "", email: "", phone: "", regime: "", message: "" });
        } catch (err) {
            toast.error("Não foi possível enviar.", {
                description: "Tente novamente ou chame a gente no WhatsApp.",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="contato" data-testid="contact-section" className="py-20 lg:py-32 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid lg:grid-cols-2 gap-14 lg:gap-20">
                    <div className="reveal-on-scroll">
                        <p className="eyebrow text-xs font-bold uppercase tracking-[0.2em] text-blue-900" data-testid="contact-eyebrow">
                            Fale com a WBS
                        </p>
                        <h1 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900" data-testid="contact-title">
                            Peça seu orçamento ou agende um diagnóstico gratuito
                        </h1>
                        <p className="mt-4 text-base text-slate-600 leading-relaxed" data-testid="contact-subtitle">
                            Preencha o formulário ou fale agora mesmo pelo canal que preferir. Respondemos rápido.
                        </p>

                        <div className="mt-10 space-y-6" data-testid="contact-info-list">
                            <a href={`tel:${BUSINESS.phoneRaw}`} data-testid="contact-phone-link" className="flex gap-4 items-center group">
                                <span className="flex items-center justify-center w-12 h-12 rounded-lg bg-[#F8FAFC] border border-slate-200 text-blue-900 transition-transform duration-300 group-hover:-translate-y-1">
                                    <Phone className="w-5 h-5" />
                                </span>
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Telefone / Ligação direta</p>
                                    <p className="text-base font-semibold text-slate-800 group-hover:text-blue-900 transition-colors duration-200">{BUSINESS.phoneDisplay}</p>
                                </div>
                            </a>
                            <a href={`${BUSINESS.whatsapp}?text=${encodeURIComponent("Olá! Vim pelo site e quero falar com a WBS.")}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="flex gap-4 items-center group">
                                <span className="flex items-center justify-center w-12 h-12 rounded-lg bg-[#F8FAFC] border border-slate-200 text-[#25D366] transition-transform duration-300 group-hover:-translate-y-1">
                                    <MessageCircle className="w-5 h-5" />
                                </span>
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">WhatsApp</p>
                                    <p className="text-base font-semibold text-slate-800 group-hover:text-blue-900 transition-colors duration-200">Resposta rápida no horário comercial</p>
                                </div>
                            </a>
                            <a href={`mailto:${BUSINESS.email}`} data-testid="contact-email-link" className="flex gap-4 items-center group">
                                <span className="flex items-center justify-center w-12 h-12 rounded-lg bg-[#F8FAFC] border border-slate-200 text-blue-900 transition-transform duration-300 group-hover:-translate-y-1">
                                    <Mail className="w-5 h-5" />
                                </span>
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">E-mail</p>
                                    <p className="text-base font-semibold text-slate-800 group-hover:text-blue-900 transition-colors duration-200">{BUSINESS.email}</p>
                                </div>
                            </a>
                            <div className="flex gap-4 items-center" data-testid="contact-address">
                                <span className="flex items-center justify-center w-12 h-12 rounded-lg bg-[#F8FAFC] border border-slate-200 text-blue-900 shrink-0">
                                    <MapPin className="w-5 h-5" />
                                </span>
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Escritório</p>
                                    <p className="text-base font-semibold text-slate-800">{BUSINESS.address}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="glass-card rounded-2xl p-8 sm:p-10 shadow-lg" data-testid="contact-form-card">
                        <h3 className="text-xl sm:text-2xl font-medium text-slate-900" data-testid="form-title">
                            Solicite uma proposta
                        </h3>
                        <p className="mt-2 text-sm text-slate-500">Diagnóstico inicial gratuito e sem compromisso.</p>
                        <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="lead-form">
                            <div className="space-y-2">
                                <Label htmlFor="lead-name">Nome completo</Label>
                                <Input
                                    id="lead-name"
                                    data-testid="lead-name-input"
                                    placeholder="Seu nome"
                                    value={form.name}
                                    onChange={(e) => handleChange("name", e.target.value)}
                                    required
                                    minLength={2}
                                    className="bg-white"
                                />
                            </div>
                            <div className="grid sm:grid-cols-2 gap-5">
                                <div className="space-y-2">
                                    <Label htmlFor="lead-email">E-mail</Label>
                                    <Input
                                        id="lead-email"
                                        type="email"
                                        data-testid="lead-email-input"
                                        placeholder="voce@empresa.com.br"
                                        value={form.email}
                                        onChange={(e) => handleChange("email", e.target.value)}
                                        required
                                        className="bg-white"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="lead-phone">Telefone / WhatsApp</Label>
                                    <Input
                                        id="lead-phone"
                                        type="tel"
                                        data-testid="lead-phone-input"
                                        placeholder="(11) 90000-0000"
                                        value={form.phone}
                                        onChange={(e) => handleChange("phone", e.target.value)}
                                        required
                                        minLength={8}
                                        className="bg-white"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Regime tributário da sua empresa</Label>
                                <Select value={form.regime} onValueChange={(v) => handleChange("regime", v)} required>
                                    <SelectTrigger data-testid="lead-regime-select" className="bg-white">
                                        <SelectValue placeholder="Selecione o regime" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {REGIMES.map((r) => (
                                            <SelectItem key={r} value={r} data-testid={`regime-option-${r.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                                {r}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="lead-message">Como podemos ajudar?</Label>
                                <Textarea
                                    id="lead-message"
                                    data-testid="lead-message-input"
                                    placeholder="Conte um pouco sobre a sua empresa e o que você precisa..."
                                    value={form.message}
                                    onChange={(e) => handleChange("message", e.target.value)}
                                    rows={4}
                                    className="bg-white resize-none"
                                />
                            </div>
                            <Button
                                type="submit"
                                disabled={loading}
                                data-testid="lead-submit-button"
                                className="btn-shine w-full rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold transition-transform duration-200 hover:-translate-y-0.5 shadow-sm hover:shadow-md animate-pulse-gold"
                            >
                                {loading ? (
                                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                ) : (
                                    <Send className="w-4 h-4 mr-2" />
                                )}
                                {loading ? "Enviando..." : "Enviar pedido de orçamento"}
                            </Button>
                            <p className="text-xs text-slate-400 text-center">
                                Seus dados são usados apenas para o contato da WBS Serviços Contábeis.
                            </p>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
};
